import { defineElement, FC, onDidLoad, useElement, useProp } from '@atomify/hooks';
import { SLIDER_PROGRESS_CELL_CONTAINER, SliderProgressElement } from '@molecules/slider-progress';
import { userStore } from '@store/user';
import { partition } from '@utilities/partition';

type Slide = {
    constructionNumberId: string;
    propertyTypeId?: string;
    status?: string;
    sliderCell: Element;
};
type Slides = Slide[];

type ComponentState = {
    slides: Slides;
    wishlist: Set<string>;
    wishlistPropertyTypes: Set<string>;
    statuses: Set<string>;
    renderedSlides: Slides;
};

const LOADING_CLASS = 'wishlist-controller--loading';

const WishlistController: FC = ({ element }) => {
    const [filterDisabled] = useProp<boolean>('filterDisabled', false);
    const [cardSelector] = useProp<string>('cardSelector', '.c-property-card');
    const [limit] = useProp<number>('limit', 10);

    const sliderElement = useElement<SliderProgressElement>('bpd-slider-progress');
    const cellContainer = useElement<HTMLElement>(`[${SLIDER_PROGRESS_CELL_CONTAINER}]`);

    let componentState: ComponentState;

    onDidLoad(async () => {
        if (!cellContainer.current || filterDisabled) return;
        const cells = Array.from(cellContainer.current.children);
        updateState(cells);

        const timeout = setTimeout(renderSlides, 100);
        userStore.subscribe(async () => {
            if (timeout) clearTimeout(timeout);
            updateState(cells);
            renderSlides();
        }, ['loggedIn']);
    });

    function updateState(cells: Element[]) {
        const fullWishlist = new Set(userStore.getState().wishlist);

        const wishlist = new Set<string>();
        const wishlistPropertyTypes = new Set<string>();

        const statuses = new Set<string>();

        const slides = cells.reduce((acc, cell) => {
            const card = cell.querySelector<HTMLElement>(cardSelector);
            const id = card?.dataset.constructionNumberId;
            const propertyTypeId = card?.dataset.propertyTypeId;
            const status = card?.dataset.displayStatus;

            if (!id) {
                console.warn('Cannot extract ID from card dataset', card);
                return acc;
            }

            if (fullWishlist.has(id)) {
                wishlist.add(id);
                if (propertyTypeId) wishlistPropertyTypes.add(propertyTypeId);
            }

            if (status && !statuses.has(status)) {
                statuses.add(status);
            }

            acc.push({ constructionNumberId: id, propertyTypeId, status, sliderCell: cell });
            return acc;
        }, [] as Slides);

        componentState = {
            slides,
            wishlist,
            wishlistPropertyTypes,
            statuses,
            renderedSlides: [],
        };
    }

    function extractWishlistSlides() {
        const { slides, wishlist } = componentState;

        if (!wishlist.size) return [[], slides];

        const [inWishlist = [], rest = []] = partition<Slide>(
            slides,
            ({ constructionNumberId }) => !!wishlist.has(constructionNumberId),
        );

        return [inWishlist, rest];
    }

    function extractWishlistSimilarSlides(slides: Slides) {
        const { wishlist, wishlistPropertyTypes } = componentState;

        if (!wishlistPropertyTypes.size) return [[], slides];

        const [wishlistSimilar = [], rest = []] = partition<Slide>(
            slides,
            ({ constructionNumberId, propertyTypeId }) => {
                if (wishlist.has(constructionNumberId)) return false;
                return !!propertyTypeId && wishlistPropertyTypes.has(propertyTypeId);
            },
        );

        return [wishlistSimilar, rest];
    }

    function evenlyDistribute(slides: Slides, maxItems: number) {
        let renderedCount = 0;
        const returnList: Slide[] = [];
        const addedIds = new Set<string>();

        let addedPropertyTypeIds: Set<string>;

        do {
            addedPropertyTypeIds = new Set<string>();

            slides.forEach((slide) => {
                const { propertyTypeId, constructionNumberId } = slide;
                if (
                    renderedCount >= maxItems ||
                    !propertyTypeId ||
                    addedPropertyTypeIds.has(propertyTypeId) ||
                    addedIds.has(constructionNumberId)
                ) {
                    return;
                }

                addedIds.add(constructionNumberId);
                addedPropertyTypeIds.add(propertyTypeId);

                returnList.push(slide);
                renderedCount++;
            });
        } while (renderedCount < maxItems && addedPropertyTypeIds.size > 0);

        return returnList;
    }

    async function renderSlides() {
        const { current: slider } = sliderElement;
        const { current: container } = cellContainer;
        if (!slider || !container || !componentState) return;
        const { renderedSlides, statuses } = componentState;

        element.classList.add(LOADING_CLASS);

        const [wishlistSlides, otherSlides] = extractWishlistSlides();

        if (wishlistSlides.length) {
            renderedSlides.push(...wishlistSlides);
        }

        for (const status of statuses) {
            const renderedCount = renderedSlides.length;
            const slidesToAddCount = limit - renderedCount;

            const statusSlides = otherSlides.filter(({ status: s }) => status === s);
            const hasOverflowSlides = statusSlides.length > slidesToAddCount;

            if (slidesToAddCount <= 0) break;
            if (!statusSlides.length) continue;

            const [similarSlides, restSlides] = extractWishlistSimilarSlides(statusSlides);
            const similarCount = similarSlides.length;

            if (similarCount < slidesToAddCount) {
                renderedSlides.push(...evenlyDistribute(similarSlides, similarCount));

                if (hasOverflowSlides) {
                    const restMax = slidesToAddCount - similarCount;
                    renderedSlides.push(...evenlyDistribute(restSlides, restMax));
                } else {
                    renderedSlides.push(...evenlyDistribute(restSlides, restSlides.length));
                }
            } else {
                renderedSlides.push(...evenlyDistribute(similarSlides, slidesToAddCount));
            }
        }

        container.innerHTML = '';

        renderedSlides
            .map(({ sliderCell }) => sliderCell.cloneNode(true))
            .forEach((clone) => container.appendChild(clone));

        if (!slider.connected) await slider.componentOnReady();
        slider.reload();

        element.classList.remove(LOADING_CLASS);
    }
};

WishlistController.props = {
    filterDisabled: {
        type: Boolean,
        reflectToAttr: true,
    },
    cardSelector: {
        type: String,
        reflectToAttr: true,
    },
    maxItems: {
        type: Number,
        reflectToAttr: true,
    },
    limit: {
        type: Number,
        reflectToAttr: true,
    },
};

defineElement('bpd-property-slider-wishlist-controller', WishlistController);
